import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import ManualLink from '../../components/ManualLink';
import { ReputationBar } from '../../components/Reputation/ReputationBar';
import { ReputationGauge } from '../../components/Reputation/ReputationGauge';
import DomainBadges from '../../components/UserProfile/DomainBadges';
import { GET_USER_BY_USERNAME_QUERY } from '../../graphql/resolvers/userQueries/getUserByUsername.gql';
import {
  DIMENSION_META,
  isPlaceholderReputation,
  vouchCaption,
} from '../../helpers/reputationView';
import { describeEffectiveScore } from '../../helpers/reputationVouchDisplay';
import './ReputationBreakdown.scss';

export default function ReputationBreakdown() {
  const { username } = useParams<{ username: string }>();
  const { data, loading } = useQuery(GET_USER_BY_USERNAME_QUERY, {
    variables: { username: username ?? '' },
    skip: !username,
  });

  if (loading) {
    return (
      <div className="reputation-breakdown reputation-breakdown--loading">
        <div className="spinner" />
      </div>
    );
  }

  const user = data?.getUserByUsername;
  const rep = user?.reputation;

  if (!user || !rep) {
    return (
      <div className="reputation-breakdown reputation-breakdown--empty">
        <p>No reputation data found for this user.</p>
      </div>
    );
  }

  if (isPlaceholderReputation(rep)) {
    return (
      <div className="reputation-breakdown reputation-breakdown--placeholder">
        <p>
          Not enough activity yet to establish a reputation. Keep publishing to
          build it.
        </p>
      </div>
    );
  }

  const scores = rep.dimensionScores as Record<string, number>;
  const confidences = rep.dimensionConfidences as Record<string, number>;
  const evidence = rep.evidence as Record<string, string[]>;

  const { effectiveShown, effectiveRaw, earnedShown, vouchedShown, hasBoost } =
    describeEffectiveScore(rep.overallScore, rep.vouchBoost ?? 0);

  return (
    <div className="reputation-breakdown">
      <div className="reputation-breakdown__topbar">
        <ManualLink section="reputation" label="How reputation works" />
      </div>
      <div className="reputation-breakdown__hero">
        <ReputationGauge score={effectiveRaw} />
        <span className="reputation-breakdown__score">{effectiveShown}</span>
        {hasBoost && (
          <span className="reputation-breakdown__vouch-caption">
            {vouchCaption(earnedShown, vouchedShown)}
          </span>
        )}
      </div>

      {rep.domains.length > 0 && (
        <div className="reputation-breakdown__domains">
          <DomainBadges domains={rep.domains} />
        </div>
      )}

      <div className="reputation-breakdown__bars">
        {DIMENSION_META.map((d) => (
          <ReputationBar
            key={d.key}
            label={d.label}
            score={scores[d.key] ?? 0}
            confidence={confidences[d.key] ?? 0}
          >
            {evidence[d.key]?.length ? (
              <ul className="reputation-breakdown__evidence">
                {evidence[d.key].map((e, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: evidence list is stable and unkeyed
                  <li key={i}>{e}</li>
                ))}
              </ul>
            ) : null}
          </ReputationBar>
        ))}
      </div>

      <footer className="reputation-breakdown__footer">
        <span>Source: {rep.source}</span>
        {rep.lastUpdated && (
          <span>
            Last updated: {new Date(rep.lastUpdated).toLocaleDateString()}
          </span>
        )}
      </footer>
    </div>
  );
}
